import * as React from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input, Field } from '../components/ui/input';

interface PromptOptions {
  title?: string;
  message?: React.ReactNode;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  inputType?: string;
}

interface PromptContextValue {
  prompt: (options: PromptOptions | string) => Promise<string | null>;
}

const PromptContext = React.createContext<PromptContextValue | null>(null);

interface PendingState {
  options: PromptOptions;
  resolve: (value: string | null) => void;
}

export function PromptProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = React.useState<PendingState | null>(null);
  const [value, setValue] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const prompt = React.useCallback<PromptContextValue['prompt']>((opts) => {
    const options: PromptOptions = typeof opts === 'string' ? { message: opts } : opts;
    setValue(options.defaultValue ?? '');
    return new Promise<string | null>((resolve) => {
      setPending({ options, resolve });
    });
  }, []);

  const handleClose = (v: string | null) => {
    if (pending) {
      pending.resolve(v);
      setPending(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleClose(value);
  };

  const ctxValue = React.useMemo(() => ({ prompt }), [prompt]);
  const options = pending?.options;

  return (
    <PromptContext.Provider value={ctxValue}>
      {children}
      <Dialog open={!!pending} onClose={() => handleClose(null)} size="md" initialFocus={inputRef}>
        <form onSubmit={handleSubmit}>
          <DialogHeader title={options?.title ?? 'Enter a value'} onClose={() => handleClose(null)} />
          <DialogBody>
            <Field label={options?.message || undefined}>
              <Input
                ref={inputRef}
                type={options?.inputType || 'text'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={options?.placeholder}
                autoFocus
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(null)}>
              {options?.cancelLabel ?? 'Cancel'}
            </Button>
            <Button type="submit">{options?.confirmLabel ?? 'OK'}</Button>
          </DialogFooter>
        </form>
      </Dialog>
    </PromptContext.Provider>
  );
}

export function usePrompt(): PromptContextValue {
  const ctx = React.useContext(PromptContext);
  if (!ctx) throw new Error('usePrompt must be used within PromptProvider');
  return ctx;
}
